import { useEffect, useRef, useState } from "react";
import { Landmark, Upload, CloudCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useApi, useResource, errorText } from "@/lib/api";
import BankPreviewTable from "@/components/bank/BankPreviewTable";
import SaveRuleDialog from "@/components/bank/SaveRuleDialog";
import PayeeRules from "@/components/bank/PayeeRules";
import ConfirmAction from "@/components/bank/ConfirmAction";
import SkippedPanel from "@/components/bank/SkippedPanel";
import ColumnMapDialog from "@/components/bank/ColumnMapDialog";

const Stat = ({ label, value, testId, onClick, active }) => (
  <div onClick={onClick} role={onClick ? "button" : undefined}
    className={`border bg-card p-4 transition-colors ${active ? "border-primary" : "border-border"} ${onClick ? "cursor-pointer hover:border-primary/60" : ""}`}>
    <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="font-mono text-xl mt-1" data-testid={testId}>{value}</div>
    {onClick && <div className="text-[11px] text-primary mt-1">{active ? "Hide" : "Review these"}</div>}
  </div>
);

export default function BankFeedPage() {
  const api = useApi();
  const input = useRef(null);
  const { data: status, reload } = useResource("/bank-feed/status");
  const { data: categories } = useResource("/categories");
  const { data: dirs } = useResource("/directors");
  const directors = (dirs || []).filter((d) => d.active);
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState([]);
  const [deleted, setDeleted] = useState([]);
  const [busy, setBusy] = useState(false);
  const [ruleRow, setRuleRow] = useState(null);
  const [rulesKey, setRulesKey] = useState(0);
  const [draft, setDraft] = useState({ loaded: false, saving: false, at: null, by: null });
  const skipSave = useRef(false);
  const [showSkipped, setShowSkipped] = useState(false);
  const [mapAsk, setMapAsk] = useState(null);
  const addBack = (s) => {
    setPreview((p) => ({ ...p, skipped: (p.skipped || []).filter((x) => x.ref !== s.ref), duplicates_skipped: Math.max(0, p.duplicates_skipped - 1) }));
    const { reason, match, ...row } = s;
    setRows((l) => [...l, { ...row, category: row.category || match?.category || null, matched: !!(row.category || match?.category), keep: true, matchedFor: row.description }]
      .sort((a, b) => a.date.localeCompare(b.date)));
    toast.success("Moved into the review");
  };
  const matchChange = (ref, patch) => setPreview((p) => ({ ...p, skipped: p.skipped.map((x) => (x.ref === ref ? { ...x, match: { ...x.match, ...patch } } : x)) }));

  // load the saved review so work can continue over several sessions
  useEffect(() => {
    api("get", "/bank-feed/draft").then((d) => {
      if (d?.preview) {
        skipSave.current = true;
        setPreview(d.preview);
        setRows(d.rows || []);
        setDeleted(d.deleted || []);
      }
      setDraft({ loaded: true, saving: false, at: d?.updated_at || null, by: d?.updated_by || null });
    }).catch(() => setDraft((s) => ({ ...s, loaded: true })));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // autosave a second after every change
  useEffect(() => {
    if (!draft.loaded || !preview) return undefined;
    if (skipSave.current) { skipSave.current = false; return undefined; }
    setDraft((s) => ({ ...s, saving: true }));
    const t = setTimeout(async () => {
      try {
        const res = await api("put", "/bank-feed/draft", { preview, rows, deleted });
        setDraft((s) => ({ ...s, saving: false, at: res.updated_at, by: null }));
      } catch (e) {
        setDraft((s) => ({ ...s, saving: false }));
        toast.error(`Draft not saved: ${errorText(e)}`);
      }
    }, 1000);
    return () => clearTimeout(t);
  }, [preview, rows, deleted, draft.loaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const discard = async () => {
    try { await api("delete", "/bank-feed/draft"); } catch (e) { toast.error(errorText(e)); return; }
    setPreview(null);
    setRows([]);
    setDeleted([]);
    setDraft((s) => ({ ...s, at: null }));
    toast.success("Draft discarded");
  };
  // apply a just-saved rule to the other lines in this preview, then look their categories up again
  const ruleSaved = async ({ match, rename }) => {
    setRuleRow(null);
    setRulesKey((k) => k + 1);
    const needle = match.toUpperCase().split(/\s+/).join(" ");
    const hits = rows.filter((r) => r.original.toUpperCase().split(/\s+/).join(" ").includes(needle));
    if (!hits.length) return;
    let cats = {};
    try {
      cats = await api("post", "/bank-feed/match", { items: hits.filter((r) => !r.manual).map((r) => ({ key: r.ref, side: r.received ? "in" : "out", description: rename })) });
    } catch { /* categories stay as they are */ }
    const set = new Set(hits.map((r) => r.ref));
    setRows((l) => l.map((r) => (!set.has(r.ref) ? r : {
      ...r, description: rename, renamed: true, renamedTo: rename, matchedFor: rename,
      ...(r.manual ? {} : { category: cats[r.ref] ?? r.category, matched: !!(cats[r.ref] ?? r.category) }),
    })));
    if (hits.length > 1) toast.success(`Rule applied to ${hits.length} lines in this file`);
  };
  const remove = (ref) => {
    const row = rows.find((r) => r.ref === ref);
    setRows((l) => l.filter((r) => r.ref !== ref));
    setDeleted((d) => [...d, row]);
  };
  const undo = () => {
    const last = deleted[deleted.length - 1];
    setDeleted((d) => d.slice(0, -1));
    setRows((l) => [...l, last].sort((a, b) => a.date.localeCompare(b.date)));
  };

  const upload = async (file, mapping) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      if (mapping) form.append("mapping", JSON.stringify(mapping));
      const p = await api("post", "/bank-feed/preview", form);
      if (p.needs_mapping) {
        setMapAsk({ ...p, file });
        setBusy(false);
        if (input.current) input.current.value = "";
        return;
      }
      setMapAsk(null);
      // card repayments are left unticked so the payment isn't counted on both the bank and the card
      const fresh = p.rows.map((r) => ({ ...r, keep: !r.repayment, matchedFor: r.description, renamedTo: r.renamed ? r.description : undefined }));
      if (fresh.some((r) => r.repayment)) toast.info(`${fresh.filter((r) => r.repayment).length} card repayment line(s) left unticked so they aren't counted twice`);
      if (preview) {
        // add to the saved draft: only lines not already in it
        const known = new Set([...rows, ...deleted].map((r) => r.ref));
        const add = fresh.filter((r) => !known.has(r.ref));
        setRows((l) => [...l, ...add].sort((a, b) => a.date.localeCompare(b.date)));
        setPreview((old) => {
          const skipped = [...(p.skipped || []).filter((s) => !known.has(s.ref)), ...(old.skipped || []).filter((s) => !(p.skipped || []).some((n) => n.ref === s.ref))];
          return { ...old, filename: old.filename.includes(p.filename) ? old.filename : `${old.filename}, ${p.filename}`,
            total_lines: old.total_lines + add.length + skipped.length - (old.skipped || []).length,
            duplicates_skipped: skipped.length,
            from: old.from < p.from ? old.from : p.from, to: old.to > p.to ? old.to : p.to,
            new_fys: [...new Set([...old.new_fys, ...p.new_fys])], accounts: [...new Set([...(old.accounts || ["CommBank"]), ...p.accounts])], rows: [], skipped };
        });
        toast.success(`Added ${add.length} new ${add.length === 1 ? "line" : "lines"} to your draft`);
      } else {
        setPreview({ ...p, rows: [] });
        setRows(fresh);
        setDeleted([]);
      }
    } catch (e) { toast.error(errorText(e)); }
    setBusy(false);
    if (input.current) input.current.value = "";
  };
  const save = async () => {
    const keep = rows.filter((r) => r.keep).map(({ keep, matched, fy, original, matchedFor, manual, renamed, renamedTo, repayment, ...r }) => r);
    if (!keep.length && !deleted.length) return toast.error("Tick at least one line");
    setBusy(true);
    try {
      const res = await api("post", "/bank-feed/commit", { rows: keep, dismissed: deleted.map((r) => r.ref) });
      toast.success(`Added ${res.inserted} lines to the ledger${res.personal ? `, ${res.personal} posted to director accounts` : ""}${res.deleted ? `, ${res.deleted} deleted` : ""}`);
      await api("delete", "/bank-feed/draft");
      setPreview(null);
      setRows([]);
      setDeleted([]);
      setDraft((s) => ({ ...s, at: null }));
      reload();
    } catch (e) { toast.error(errorText(e)); }
    setBusy(false);
  };
  const kept = rows.filter((r) => r.keep);

  return (
    <div className="rise">
      <h1 className="font-display text-4xl sm:text-5xl font-semibold" data-testid="bank-feed-title">Bank feed</h1>
      <p className="mt-3 text-muted-foreground text-base md:text-lg">Upload a CSV from CommBank, AMEX or any other bank. Each line remembers which account it came from. Lines already in the ledger are skipped, and categories are filled in from your past rows. Edit any description before adding, or delete lines you never want. Mark a director's personal spending as Personal and it goes to their account only.</p>
      <div className="mt-8 border border-border bg-card p-6 flex flex-wrap items-center gap-5">
        <div className="h-11 w-11 rounded-full bg-primary/10 text-primary grid place-items-center"><Landmark className="h-5 w-5" /></div>
        <div>
          <div className="text-sm text-muted-foreground">Where you stopped last time</div>
          <div className="font-display text-xl font-semibold" data-testid="bank-last-date">
            {status?.last_bank_date ? `Up to ${status.last_bank_date}` : "No bank lines imported yet"}
          </div>
          {status?.accounts?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2" data-testid="bank-accounts">
              {status.accounts.map((a) => (
                <span key={a.name} className="text-xs rounded-full border border-border px-2.5 py-1" data-testid={`bank-account-${a.name}`}>
                  <span className="font-medium">{a.name}</span> <span className="text-muted-foreground">up to {a.last_date}</span>
                </span>
              ))}
            </div>
          )}
        </div>
        <input ref={input} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="bank-file-input" />
        {mapAsk && <ColumnMapDialog info={mapAsk} busy={busy} onClose={() => setMapAsk(null)} onSubmit={(m) => upload(mapAsk.file, m)} />}
        <Button className="ml-auto rounded-full" disabled={busy} onClick={() => input.current?.click()} data-testid="bank-upload-button">
          <Upload className="h-4 w-4 mr-2" /> {busy && !preview ? "Reading…" : preview ? "Add another CSV to draft" : "Upload bank CSV"}
        </Button>
      </div>
      {preview && (
        <div className="mt-8 space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Lines in file" value={preview.total_lines} testId="bank-stat-total" />
            <Stat label="Already in ledger" value={preview.duplicates_skipped} testId="bank-stat-skipped"
              onClick={() => setShowSkipped((v) => !v)} active={showSkipped} />
            <Stat label="Lines to review" value={rows.length} testId="bank-stat-new" />
            <Stat label="Still need a category" value={kept.filter((r) => !r.category && !r.personal_director).length} testId="bank-stat-blank" />
          </div>
          <div className="flex flex-wrap items-center gap-3 border border-amber-500/40 bg-amber-500/5 p-4" data-testid="bank-preview-bar">
            <span className="text-sm"><span data-testid="bank-preview-accounts">{(preview.accounts || ["CommBank"]).join(" + ")}</span> · {preview.filename}: {preview.from} to {preview.to}. Your review is saved here as a draft; nothing reaches the ledger until you press Add.
              {preview.new_fys.length > 0 && ` New financial year ${preview.new_fys.join(", ")} will be set up.`}</span>
            <span className="text-xs text-muted-foreground inline-flex items-center gap-1" data-testid="bank-draft-status">
              {draft.saving ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Saving draft…</>
                : draft.at ? <><CloudCheck className="h-3.5 w-3.5 text-emerald-500" />Draft saved {new Date(draft.at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}{draft.by ? ` by ${draft.by}` : ""}</> : null}
            </span>
            {deleted.length > 0 && (
              <span className="text-sm text-rose-500" data-testid="bank-deleted-count">
                {deleted.length} deleted <button type="button" className="underline ml-1" onClick={undo} data-testid="bank-undo-delete">Undo</button>
              </span>
            )}
            <span className="ml-auto"><ConfirmAction label="Discard draft" title="Discard this bank feed draft?"
              body="All edits in this review are thrown away. Nothing in the ledger changes." onConfirm={discard} testId="bank-discard-button" /></span>
            <Button disabled={busy || (!kept.length && !deleted.length)} onClick={save} data-testid="bank-commit-button">
              {busy ? "Saving…" : kept.length ? `Add ${kept.length} ${kept.length === 1 ? "line" : "lines"} to ledger` : "Save deletions"}
            </Button>
          </div>
          {showSkipped && <SkippedPanel skipped={preview.skipped} categories={categories} onAddBack={addBack} onMatchChange={matchChange} />}
          {rows.length ? <BankPreviewTable rows={rows} setRows={setRows} categories={categories} directors={directors} onDelete={remove} onSaveRule={setRuleRow} />
            : <p className="text-muted-foreground" data-testid="bank-nothing-new">Everything in this file is already in the ledger.</p>}
        </div>
      )}
      {ruleRow && <SaveRuleDialog row={ruleRow} onClose={() => setRuleRow(null)} onSaved={ruleSaved} />}
      <div className="mt-12"><PayeeRules key={rulesKey} /></div>
    </div>
  );
}
